import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Pagination, Row, Spinner, Table } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import ThaiDateInput from '../components/ThaiDateInput'
import { toISODate } from '../utils/thaiDate'

const ASSET_STATUS = [
  ['active', 'พร้อมขึ้นทะเบียน', 'secondary'],
  ['in_use', 'ใช้งานอยู่', 'success'],
  ['repair', 'ซ่อมบำรุง', 'warning'],
  ['lost', 'สูญหาย', 'danger'],
  ['disposed', 'จำหน่ายแล้ว', 'dark'],
  ['borrowed', 'ยืมใช้งาน', 'info'],
]

const REQUEST_STATUS = [
  ['draft', 'ร่าง', 'secondary'],
  ['submitted', 'เสนอขอซื้อ', 'primary'],
  ['approved', 'อนุมัติ', 'success'],
  ['ordered', 'สั่งซื้อแล้ว', 'info'],
  ['received', 'รับของแล้ว', 'success'],
  ['cancelled', 'ยกเลิก', 'danger'],
]

const statusMap = (items) => Object.fromEntries(items.map(([key, label, color]) => [key, { label, color }]))
const assetStatus = statusMap(ASSET_STATUS)
const requestStatus = statusMap(REQUEST_STATUS)

const fmtMoney = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtDate = (d) => d ? String(d).slice(0, 10) : '-'
const toDateInput = (d) => { const v = fmtDate(d); return v === '-' ? '' : v }
const today = () => toISODate(new Date())

const getWeekRange = () => {
  const d = new Date()
  const day = d.getDay()
  const mon = new Date(d); mon.setDate(d.getDate() - (day === 0 ? 6 : day - 1))
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6)
  return [toISODate(mon), toISODate(sun)]
}

const getMonthRange = () => {
  const d = new Date()
  const first = new Date(d.getFullYear(), d.getMonth(), 1)
  const last  = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  return [toISODate(first), toISODate(last)]
}

const PAGE_SIZES = [10, 20, 50, 100]
const DEFAULT_PAGE_SIZE = 20

function usePagination(data, defaultSize = DEFAULT_PAGE_SIZE) {
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(defaultSize)
  useEffect(() => { setPage(1) }, [data])
  const total      = data.length
  const totalPages = Math.max(1, Math.ceil(total / size))
  const safePage   = Math.min(page, totalPages)
  const start      = (safePage - 1) * size
  const slice      = data.slice(start, start + size)
  const changeSize = (s) => { setSize(s); setPage(1) }
  return { page: safePage, setPage, size, changeSize, totalPages, slice, total, start }
}

function PaginationBar({ page, setPage, totalPages, total, start, size, changeSize }) {
  if (total === 0) return null
  const end = Math.min(start + size, total)
  const pageNums = []
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= 2) pageNums.push(i)
  }
  const items = []
  let prev = 0
  for (const p of pageNums) {
    if (prev && p - prev > 1) items.push(<Pagination.Ellipsis key={`e${p}`} disabled />)
    items.push(<Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>)
    prev = p
  }
  return (
    <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top flex-wrap gap-2">
      <div className="d-flex align-items-center gap-2">
        <span className="text-muted fs-sm">แสดง {start + 1}–{end} จาก {total} รายการ</span>
        <Form.Select size="sm" style={{ width: 80 }} value={size} onChange={e => changeSize(Number(e.target.value))}>
          {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
        </Form.Select>
        <span className="text-muted fs-sm">ต่อหน้า</span>
      </div>
      <Pagination size="sm" className="mb-0">
        <Pagination.Prev disabled={page === 1} onClick={() => setPage(p => p - 1)} />
        {items}
        <Pagination.Next disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
      </Pagination>
    </div>
  )
}

function DateRangeFilter({ dateFrom, setDateFrom, dateTo, setDateTo, children }) {
  const set = (from, to) => { setDateFrom(from); setDateTo(to) }
  const todayStr = today()
  const [wMon, wSun] = getWeekRange()
  const [mFirst, mLast] = getMonthRange()
  const hasRange = dateFrom || dateTo
  return (
    <Row className="g-2 align-items-end">
      {children}
      <Col xs="auto">
        <Form.Label className="mb-1 fw-semibold">ช่วงด่วน</Form.Label>
        <div className="d-flex gap-1">
          <Button size="sm" variant={dateFrom === todayStr && dateTo === todayStr ? 'primary' : 'outline-secondary'} onClick={() => set(todayStr, todayStr)}>วันนี้</Button>
          <Button size="sm" variant={dateFrom === wMon && dateTo === wSun ? 'primary' : 'outline-secondary'} onClick={() => set(wMon, wSun)}>สัปดาห์นี้</Button>
          <Button size="sm" variant={dateFrom === mFirst && dateTo === mLast ? 'primary' : 'outline-secondary'} onClick={() => set(mFirst, mLast)}>เดือนนี้</Button>
        </div>
      </Col>
      <Col xs="auto">
        <Form.Label className="mb-1 fw-semibold">ตั้งแต่</Form.Label>
        <ThaiDateInput value={dateFrom} onChange={v => setDateFrom(v)} style={{ width: 155 }} />
      </Col>
      <Col xs="auto">
        <Form.Label className="mb-1 fw-semibold">ถึง</Form.Label>
        <ThaiDateInput value={dateTo} onChange={v => setDateTo(v)} style={{ width: 155 }} />
      </Col>
      {hasRange && (
        <Col xs="auto">
          <Button variant="outline-danger" size="sm" style={{ marginTop: 24 }} onClick={() => { setDateFrom(''); setDateTo('') }} title="ล้างวันที่">
            <i className="ri-close-line" />
          </Button>
        </Col>
      )}
    </Row>
  )
}

function StatusBadge({ value, map }) {
  const item = map[value] || { label: value || '-', color: 'secondary' }
  return <Badge bg={item.color}>{item.label}</Badge>
}

function PageShell({ title, icon, subtitle, children, action }) {
  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div>
              <h4 className="main-title mb-1"><i className={`${icon} me-2 text-primary`} />{title}</h4>
              {subtitle && <div className="text-muted fs-sm">{subtitle}</div>}
            </div>
            {action}
          </div>
          {children}
        </Container>
        <Footer />
      </div>
    </>
  )
}

function StatCard({ icon, label, value, color = 'primary', hint }) {
  return (
    <Col sm={6} xl={3}>
      <Card className="card-one shadow-sm h-100">
        <Card.Body className="d-flex align-items-center gap-3">
          <div className={`rounded-3 bg-${color} bg-opacity-10 text-${color} d-flex align-items-center justify-content-center`} style={{ width: 52, height: 52, fontSize: 26 }}>
            <i className={icon} />
          </div>
          <div>
            <div className="text-muted fs-sm">{label}</div>
            <h3 className="mb-0">{value}</h3>
            {hint && <div className="fs-xs text-muted mt-1">{hint}</div>}
          </div>
        </Card.Body>
      </Card>
    </Col>
  )
}

function useInventoryData() {
  const [master, setMaster] = useState({ categories: [], locations: [], vendors: [], teachers: [] })
  const [error, setError] = useState('')
  const loadMaster = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/inventory/master-data')
      setMaster(res.data.data || { categories: [], locations: [], vendors: [], teachers: [] })
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลอ้างอิงไม่สำเร็จ')
    }
  }, [])
  useEffect(() => { loadMaster() }, [loadMaster])
  return { master, loadMaster, error, setError }
}

export function InventoryDashboard() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get('/api/inventory/dashboard')
      setData(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลด dashboard พัสดุครุภัณฑ์ไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const categoryChart = useMemo(() => ({
    series: [{ name: 'จำนวน', data: (data?.categoryRows || []).map(r => Number(r.total || 0)) }],
    options: {
      chart: { type: 'bar', toolbar: { show: false } },
      colors: ['#0d6efd'],
      plotOptions: { bar: { borderRadius: 4, horizontal: true } },
      dataLabels: { enabled: false },
      xaxis: { categories: (data?.categoryRows || []).map(r => r.category_name) },
    },
  }), [data])

  const statusChart = useMemo(() => ({
    series: (data?.statusRows || []).map(r => Number(r.total || 0)),
    options: {
      chart: { type: 'donut', toolbar: { show: false } },
      labels: (data?.statusRows || []).map(r => assetStatus[r.asset_status]?.label || r.asset_status),
      legend: { position: 'bottom' },
    },
  }), [data])

  return (
    <PageShell title="Dashboard งานพัสดุครุภัณฑ์" icon="ri-archive-drawer-line" subtitle="ภาพรวมทะเบียนครุภัณฑ์ การจัดซื้อ และงานซ่อมบำรุงของโรงเรียน">
      {error && <Alert variant="danger">{error}</Alert>}
      {loading ? <div className="text-center py-5"><Spinner animation="border" /></div> : (
        <>
          <Row className="g-3 mb-4">
            <StatCard icon="ri-archive-line" label="ครุภัณฑ์ทั้งหมด" value={Number(data?.assetStats?.total_assets || 0).toLocaleString('th-TH')} color="primary" />
            <StatCard icon="ri-money-dollar-circle-line" label="มูลค่ารวม" value={fmtMoney(data?.assetStats?.total_value)} color="success" hint="บาท" />
            <StatCard icon="ri-shopping-cart-2-line" label="คำขอจัดซื้อที่เดินงาน" value={Number(data?.requestStats?.active_requests || 0).toLocaleString('th-TH')} color="info" />
          </Row>
          <Row className="g-3 mb-4">
            <Col lg={7}>
              <Card className="card-one shadow-sm h-100"><Card.Header><Card.Title as="h6">ครุภัณฑ์ตามหมวดหมู่</Card.Title></Card.Header><Card.Body><ReactApexChart options={categoryChart.options} series={categoryChart.series} type="bar" height={310} /></Card.Body></Card>
            </Col>
            <Col lg={5}>
              <Card className="card-one shadow-sm h-100"><Card.Header><Card.Title as="h6">สถานะครุภัณฑ์</Card.Title></Card.Header><Card.Body>{statusChart.series.length ? <ReactApexChart options={statusChart.options} series={statusChart.series} type="donut" height={310} /> : <div className="text-muted text-center py-5">ยังไม่มีข้อมูลครุภัณฑ์</div>}</Card.Body></Card>
            </Col>
          </Row>
          <Card className="card-one shadow-sm">
            <Card.Header><Card.Title as="h6">ครุภัณฑ์ล่าสุด</Card.Title></Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead><tr><th>รหัส</th><th>รายการ</th><th>หมวดหมู่</th><th>สถานที่</th><th>มูลค่า</th><th>สถานะ</th></tr></thead>
                <tbody>{(data?.recentAssets || []).map(row => <tr key={row.asset_id}><td className="fw-semibold">{row.asset_code}</td><td>{row.asset_name}</td><td>{row.category_name || '-'}</td><td>{row.loc_name || '-'}</td><td>{fmtMoney(row.purchase_price)}</td><td><StatusBadge value={row.asset_status} map={assetStatus} /></td></tr>)}</tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}
    </PageShell>
  )
}

function AssetModal({ show, onHide, onSaved, row, master }) {
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => { setForm(row || { asset_status: 'active', acquire_date: today(), purchase_price: 0 }); setError('') }, [row, show])
  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }))
  const save = async () => {
    setSaving(true)
    setError('')
    try {
      await axiosInstance.post('/api/inventory/assets', form)
      onSaved()
      onHide()
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally { setSaving(false) }
  }
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton><Modal.Title><i className="ri-archive-line me-2" />{form.asset_id ? 'แก้ไขครุภัณฑ์' : 'เพิ่มครุภัณฑ์'}</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
        <Row className="g-3">
          <Col md={4}><Form.Label>รหัสครุภัณฑ์ *</Form.Label><Form.Control value={form.asset_code || ''} onChange={e => set('asset_code', e.target.value)} /></Col>
          <Col md={8}><Form.Label>ชื่อรายการ *</Form.Label><Form.Control value={form.asset_name || ''} onChange={e => set('asset_name', e.target.value)} /></Col>
          <Col md={4}><Form.Label>หมวดหมู่</Form.Label><Form.Select value={form.category_id || ''} onChange={e => set('category_id', e.target.value)}><option value="">ไม่ระบุ</option>{master.categories.map(c => <option key={c.category_id} value={c.category_id}>{c.category_name}</option>)}</Form.Select></Col>
          <Col md={4}><Form.Label>สถานที่</Form.Label><Form.Select value={form.loc_id || ''} onChange={e => set('loc_id', e.target.value)}><option value="">ไม่ระบุ</option>{master.locations.map(l => <option key={l.loc_id} value={l.loc_id}>{l.loc_name}</option>)}</Form.Select></Col>
          <Col md={4}><Form.Label>ผู้ขาย</Form.Label><Form.Select value={form.vendor_id || ''} onChange={e => set('vendor_id', e.target.value)}><option value="">ไม่ระบุ</option>{master.vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}</Form.Select></Col>
          <Col md={4}><Form.Label>ยี่ห้อ</Form.Label><Form.Control value={form.brand || ''} onChange={e => set('brand', e.target.value)} /></Col>
          <Col md={4}><Form.Label>รุ่น</Form.Label><Form.Control value={form.model || ''} onChange={e => set('model', e.target.value)} /></Col>
          <Col md={4}><Form.Label>Serial No.</Form.Label><Form.Control value={form.serial_no || ''} onChange={e => set('serial_no', e.target.value)} /></Col>
          <Col md={3}><Form.Label>วันที่ได้มา</Form.Label><ThaiDateInput value={toDateInput(form.acquire_date)} onChange={v => set('acquire_date', v)} /></Col>
          <Col md={3}><Form.Label>ปีงบประมาณ</Form.Label><Form.Control value={form.fiscal_year || ''} onChange={e => set('fiscal_year', e.target.value)} /></Col>
          <Col md={3}><Form.Label>มูลค่า (บาท)</Form.Label><Form.Control type="number" value={form.purchase_price || 0} onChange={e => set('purchase_price', e.target.value)} /></Col>
          <Col md={3}><Form.Label>รับประกันถึง</Form.Label><ThaiDateInput value={toDateInput(form.warranty_until)} onChange={v => set('warranty_until', v)} /></Col>
          <Col md={4}><Form.Label>สถานะ</Form.Label><Form.Select value={form.asset_status || 'active'} onChange={e => set('asset_status', e.target.value)}>{ASSET_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Form.Select></Col>
          <Col md={4}><Form.Label>ผู้รับผิดชอบ</Form.Label><Form.Select value={form.responsible_teacher_id || ''} onChange={e => set('responsible_teacher_id', e.target.value)}><option value="">ไม่ระบุ</option>{master.teachers.map(t => <option key={t.t_id} value={t.t_id}>{t.t_name}</option>)}</Form.Select></Col>
          <Col md={4}><Form.Label>แหล่งงบประมาณ</Form.Label><Form.Control value={form.budget_source || ''} onChange={e => set('budget_source', e.target.value)} /></Col>
          <Col md={12}><Form.Label>หมายเหตุ</Form.Label><Form.Control as="textarea" rows={2} value={form.asset_note || ''} onChange={e => set('asset_note', e.target.value)} /></Col>
        </Row>
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={onHide}>ปิด</Button><Button onClick={save} disabled={saving || !form.asset_code || !form.asset_name}>{saving ? 'กำลังบันทึก...' : 'บันทึก'}</Button></Modal.Footer>
    </Modal>
  )
}

export function InventoryAssets() {
  const { master, error: masterError } = useInventoryData()
  const [rows, setRows] = useState([])
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState({ show: false, row: null })
  const [error, setError] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const q = new URLSearchParams()
      if (search) q.set('search', search)
      if (status) q.set('status', status)
      const res = await axiosInstance.get(`/api/inventory/assets?${q}`)
      setRows(res.data.data || [])
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดรายการครุภัณฑ์ไม่สำเร็จ')
    } finally { setLoading(false) }
  }, [search, status])

  useEffect(() => { load() }, [load])

  return (
    <PageShell title="ทะเบียนครุภัณฑ์" icon="ri-archive-line" subtitle="เพิ่ม แก้ไข และติดตามสถานะครุภัณฑ์" action={<Button onClick={() => setModal({ show: true, row: null })}><i className="ri-add-line me-1" />เพิ่มครุภัณฑ์</Button>}>
      {(error || masterError) && <Alert variant="danger">{error || masterError}</Alert>}
      <Card className="card-one shadow-sm mb-3"><Card.Body><Row className="g-2"><Col md={8}><Form.Control placeholder="ค้นหารหัส / ชื่อ / serial / ยี่ห้อ" value={searchInput} onChange={e => setSearchInput(e.target.value)} /></Col><Col md={4}><Form.Select value={status} onChange={e => setStatus(e.target.value)}><option value="">ทุกสถานะ</option>{ASSET_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Form.Select></Col></Row></Card.Body></Card>
      <Card className="card-one shadow-sm"><Card.Body className="p-0">{loading ? <div className="text-center py-5"><Spinner /></div> : <Table responsive hover className="mb-0"><thead><tr><th>รหัส</th><th>รายการ</th><th>หมวดหมู่</th><th>สถานที่</th><th>ผู้รับผิดชอบ</th><th>มูลค่า</th><th>สถานะ</th><th></th></tr></thead><tbody>{rows.map(row => <tr key={row.asset_id}><td className="fw-semibold">{row.asset_code}</td><td>{row.asset_name}<div className="text-muted fs-xs">{[row.brand, row.model, row.serial_no].filter(Boolean).join(' / ')}</div></td><td>{row.category_name || '-'}</td><td>{row.loc_name || '-'}</td><td>{row.responsible_teacher_name || '-'}</td><td>{fmtMoney(row.purchase_price)}</td><td><StatusBadge value={row.asset_status} map={assetStatus} /></td><td className="text-end"><Button size="sm" variant="outline-primary" onClick={() => setModal({ show: true, row })}>แก้ไข</Button></td></tr>)}</tbody></Table>}</Card.Body></Card>
      <AssetModal show={modal.show} row={modal.row} master={master} onHide={() => setModal({ show: false, row: null })} onSaved={load} />
    </PageShell>
  )
}

function SimpleModal({ show, onHide, title, children, onSave, saving, disabled, error }) {
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton><Modal.Title>{title}</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
        {children}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={onHide}>ปิด</Button><Button onClick={onSave} disabled={saving || disabled}>{saving ? 'กำลังบันทึก...' : 'บันทึก'}</Button></Modal.Footer>
    </Modal>
  )
}

export function InventoryProcurement() {
  const { master } = useInventoryData()
  const [rows, setRows] = useState([])
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [form, setForm] = useState({})
  const [show, setShow] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/inventory/procurement-requests')
      setRows(res.data.data || [])
    } catch (err) { setError(err.response?.data?.message || 'โหลดรายการไม่สำเร็จ') }
  }, [])

  useEffect(() => { load() }, [load])

  const filtered = useMemo(() => rows.filter(r => {
    if (statusFilter && r.request_status !== statusFilter) return false
    const d = fmtDate(r.request_date)
    if (dateFrom && d < dateFrom) return false
    if (dateTo && d > dateTo) return false
    return true
  }), [rows, statusFilter, dateFrom, dateTo])

  const pag = usePagination(filtered)

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      await axiosInstance.post('/api/inventory/procurement-requests', form)
      setShow(false)
      load()
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally { setSaving(false) }
  }

  return (
    <PageShell title="คำขอจัดซื้อ/จัดจ้าง" icon="ri-shopping-cart-2-line" subtitle="ติดตามคำขอจัดซื้อ งบประมาณ และสถานะการรับของ" action={<Button onClick={() => { setForm({ request_no: `PR-${Date.now()}`, request_date: today(), request_status: 'draft' }); setError(''); setShow(true) }}><i className="ri-add-line me-1" />เพิ่มคำขอ</Button>}>
      {error && !show && <Alert variant="danger">{error}</Alert>}
      <Card className="card-one shadow-sm mb-3">
        <Card.Body>
          <DateRangeFilter dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}>
            <Col xs="auto">
              <Form.Label className="mb-1 fw-semibold">สถานะ</Form.Label>
              <Form.Select style={{ width: 180 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">ทุกสถานะ</option>
                {REQUEST_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </Form.Select>
            </Col>
          </DateRangeFilter>
        </Card.Body>
      </Card>
      <Card className="card-one shadow-sm">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead><tr><th>เลขที่</th><th>เรื่อง</th><th>วันที่</th><th>ผู้ขอ</th><th>งบประมาณ</th><th>สถานะ</th><th></th></tr></thead>
            <tbody>
              {filtered.length === 0
                ? <tr><td colSpan={7} className="text-center text-muted py-4">ไม่พบรายการ</td></tr>
                : pag.slice.map(r => (
                  <tr key={r.request_id}>
                    <td className="fw-semibold">{r.request_no}</td>
                    <td>{r.request_title}</td>
                    <td>{fmtDate(r.request_date)}</td>
                    <td>{r.requester_name || '-'}</td>
                    <td>{fmtMoney(r.estimated_budget)}</td>
                    <td><StatusBadge value={r.request_status} map={requestStatus} /></td>
                    <td className="text-end"><Button size="sm" variant="outline-primary" onClick={() => { setForm(r); setError(''); setShow(true) }}>แก้ไข</Button></td>
                  </tr>
                ))}
            </tbody>
          </Table>
          <PaginationBar {...pag} />
        </Card.Body>
      </Card>
      <SimpleModal show={show} onHide={() => setShow(false)} title="บันทึกคำขอจัดซื้อ/จัดจ้าง" onSave={save} saving={saving} disabled={!form.request_no || !form.request_title || !form.request_date} error={error}>
        <Row className="g-3">
          <Col md={4}><Form.Label>เลขที่คำขอ *</Form.Label><Form.Control value={form.request_no || ''} onChange={e => set('request_no', e.target.value)} /></Col>
          <Col md={8}><Form.Label>เรื่อง *</Form.Label><Form.Control value={form.request_title || ''} onChange={e => set('request_title', e.target.value)} /></Col>
          <Col md={4}><Form.Label>วันที่ *</Form.Label><ThaiDateInput value={toDateInput(form.request_date)} onChange={v => set('request_date', v)} /></Col>
          <Col md={4}><Form.Label>ปีงบประมาณ</Form.Label><Form.Control value={form.budget_year || ''} onChange={e => set('budget_year', e.target.value)} /></Col>
          <Col md={4}><Form.Label>วงเงินประมาณการ</Form.Label><Form.Control type="number" value={form.estimated_budget || 0} onChange={e => set('estimated_budget', e.target.value)} /></Col>
          <Col md={6}><Form.Label>ผู้ขอ</Form.Label><Form.Select value={form.requester_teacher_id || ''} onChange={e => set('requester_teacher_id', e.target.value)}><option value="">ไม่ระบุ</option>{master.teachers.map(t => <option key={t.t_id} value={t.t_id}>{t.t_name}</option>)}</Form.Select></Col>
          <Col md={6}><Form.Label>สถานะ</Form.Label><Form.Select value={form.request_status || 'draft'} onChange={e => set('request_status', e.target.value)}>{REQUEST_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Form.Select></Col>
          <Col md={12}><Form.Label>เหตุผล/วัตถุประสงค์</Form.Label><Form.Control as="textarea" rows={3} value={form.purpose || ''} onChange={e => set('purpose', e.target.value)} /></Col>
        </Row>
      </SimpleModal>
    </PageShell>
  )
}


export function InventoryVendors() {
  const [rows, setRows] = useState([])
  const [form, setForm] = useState({})
  const [show, setShow] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/inventory/vendors')
      setRows(res.data.data || [])
    } catch (err) { setError(err.response?.data?.message || 'โหลดรายการไม่สำเร็จ') }
  }, [])

  useEffect(() => { load() }, [load])

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      await axiosInstance.post('/api/inventory/vendors', form)
      setShow(false)
      load()
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally { setSaving(false) }
  }

  return (
    <PageShell title="ผู้ขาย/คู่ค้า" icon="ri-store-2-line" subtitle="ข้อมูลร้านค้า บริษัท และผู้ให้บริการซ่อม" action={<Button onClick={() => { setForm({ vendor_status: 'active' }); setError(''); setShow(true) }}>เพิ่มผู้ขาย</Button>}>
      {error && !show && <Alert variant="danger">{error}</Alert>}
      <Card className="card-one shadow-sm"><Card.Body className="p-0"><Table responsive hover className="mb-0"><thead><tr><th>ชื่อผู้ขาย</th><th>ผู้ติดต่อ</th><th>โทร</th><th>Email</th><th>สถานะ</th><th></th></tr></thead><tbody>{rows.map(r => <tr key={r.vendor_id}><td className="fw-semibold">{r.vendor_name}</td><td>{r.contact_name || '-'}</td><td>{r.phone || '-'}</td><td>{r.email || '-'}</td><td><Badge bg={r.vendor_status === 'active' ? 'success' : 'secondary'}>{r.vendor_status === 'active' ? 'ใช้งาน' : 'ปิดใช้งาน'}</Badge></td><td className="text-end"><Button size="sm" variant="outline-primary" onClick={() => { setForm(r); setError(''); setShow(true) }}>แก้ไข</Button></td></tr>)}</tbody></Table></Card.Body></Card>
      <SimpleModal show={show} onHide={() => setShow(false)} title="บันทึกผู้ขาย/คู่ค้า" onSave={save} saving={saving} disabled={!form.vendor_name} error={error}>
        <Row className="g-3">
          <Col md={8}><Form.Label>ชื่อผู้ขาย *</Form.Label><Form.Control value={form.vendor_name || ''} onChange={e => set('vendor_name', e.target.value)} /></Col>
          <Col md={4}><Form.Label>เลขผู้เสียภาษี</Form.Label><Form.Control value={form.tax_id || ''} onChange={e => set('tax_id', e.target.value)} /></Col>
          <Col md={4}><Form.Label>ผู้ติดต่อ</Form.Label><Form.Control value={form.contact_name || ''} onChange={e => set('contact_name', e.target.value)} /></Col>
          <Col md={4}><Form.Label>โทร</Form.Label><Form.Control value={form.phone || ''} onChange={e => set('phone', e.target.value)} /></Col>
          <Col md={4}><Form.Label>Email</Form.Label><Form.Control value={form.email || ''} onChange={e => set('email', e.target.value)} /></Col>
          <Col md={4}><Form.Label>สถานะ</Form.Label><Form.Select value={form.vendor_status || 'active'} onChange={e => set('vendor_status', e.target.value)}><option value="active">ใช้งาน</option><option value="inactive">ปิดใช้งาน</option></Form.Select></Col>
          <Col md={12}><Form.Label>ที่อยู่</Form.Label><Form.Control as="textarea" rows={2} value={form.address || ''} onChange={e => set('address', e.target.value)} /></Col>
        </Row>
      </SimpleModal>
    </PageShell>
  )
}

export function InventorySettings() {
  const { master, loadMaster } = useInventoryData()
  const [categoryForm, setCategoryForm] = useState({})
  const [locationForm, setLocationForm] = useState({})
  const [savingCat, setSavingCat] = useState(false)
  const [savingLoc, setSavingLoc] = useState(false)
  const [error, setError] = useState('')

  const saveCategory = async () => {
    setSavingCat(true)
    setError('')
    try {
      await axiosInstance.post('/api/inventory/categories', categoryForm)
      setCategoryForm({})
      loadMaster()
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกหมวดหมู่ไม่สำเร็จ')
    } finally { setSavingCat(false) }
  }

  const saveLocation = async () => {
    setSavingLoc(true)
    setError('')
    try {
      await axiosInstance.post('/api/inventory/locations', locationForm)
      setLocationForm({})
      loadMaster()
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกสถานที่ไม่สำเร็จ')
    } finally { setSavingLoc(false) }
  }

  return (
    <PageShell title="ตั้งค่างานพัสดุครุภัณฑ์" icon="ri-database-2-line" subtitle="หมวดหมู่ครุภัณฑ์และสถานที่จัดเก็บ">
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      <Row className="g-3">
        <Col lg={6}>
          <Card className="card-one shadow-sm">
            <Card.Header><Card.Title as="h6">หมวดหมู่ครุภัณฑ์</Card.Title></Card.Header>
            <Card.Body>
              <Row className="g-2 mb-3">
                <Col md={3}><Form.Control placeholder="รหัส" value={categoryForm.category_code || ''} onChange={e => setCategoryForm(p => ({ ...p, category_code: e.target.value }))} /></Col>
                <Col md={5}><Form.Control placeholder="ชื่อหมวดหมู่" value={categoryForm.category_name || ''} onChange={e => setCategoryForm(p => ({ ...p, category_name: e.target.value }))} /></Col>
                <Col md={2}><Form.Control type="number" placeholder="ปีเสื่อม" value={categoryForm.depreciation_years || ''} onChange={e => setCategoryForm(p => ({ ...p, depreciation_years: e.target.value }))} /></Col>
                <Col md={2}><Button className="w-100" onClick={saveCategory} disabled={savingCat || !categoryForm.category_code || !categoryForm.category_name}>{savingCat ? '...' : 'บันทึก'}</Button></Col>
              </Row>
              <Table responsive hover>
                <tbody>
                  {master.categories.map(c => (
                    <tr key={c.category_id}>
                      <td className="fw-semibold">{c.category_code}</td>
                      <td>{c.category_name}</td>
                      <td>{c.depreciation_years || '-'} ปี</td>
                      <td className="text-end"><Button size="sm" variant="outline-primary" onClick={() => setCategoryForm(c)}>แก้ไข</Button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="card-one shadow-sm">
            <Card.Header><Card.Title as="h6">สถานที่จัดเก็บ</Card.Title></Card.Header>
            <Card.Body>
              <Row className="g-2 mb-3">
                <Col md={3}><Form.Control placeholder="รหัส" value={locationForm.loc_code || ''} onChange={e => setLocationForm(p => ({ ...p, loc_code: e.target.value }))} /></Col>
                <Col md={4}><Form.Control placeholder="ชื่อสถานที่" value={locationForm.loc_name || ''} onChange={e => setLocationForm(p => ({ ...p, loc_name: e.target.value }))} /></Col>
                <Col md={3}><Form.Control placeholder="อาคาร" value={locationForm.building_name || ''} onChange={e => setLocationForm(p => ({ ...p, building_name: e.target.value }))} /></Col>
                <Col md={2}><Button className="w-100" onClick={saveLocation} disabled={savingLoc || !locationForm.loc_code || !locationForm.loc_name}>{savingLoc ? '...' : 'บันทึก'}</Button></Col>
              </Row>
              <Table responsive hover>
                <tbody>
                  {master.locations.map(l => (
                    <tr key={l.loc_id}>
                      <td className="fw-semibold">{l.loc_code}</td>
                      <td>{l.loc_name}</td>
                      <td>{l.building_name || '-'}</td>
                      <td className="text-end"><Button size="sm" variant="outline-primary" onClick={() => setLocationForm(l)}>แก้ไข</Button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </PageShell>
  )
}
